import React, { Component } from 'react'
import { Field, FieldArray } from 'redux-form'
import { Input } from 'components/Input/Input'
import SelectPretty from 'components/Input/SelectPretty'
import CheckboxRow from 'components/Input/CheckboxRow'
import { Checkbox } from 'components/Input/Checkbox'
import FieldArrayRows from 'components/Input/FieldArrayRows'
import Button from 'components/Button'
import { Row, Col } from 'components/Layout'
import cn from 'classnames'
import sLay from 'styles/modules/layout.scss'
import sText from 'components/Text/Text.scss'
import sMarg from 'styles/modules/margins.scss'
import {
  vRequired,
  vChecked,
  vStandardNumber,
  vMaxArrayLength,
  vMinArrayLength,
  v24HourTime,
} from 'components/Input/Input.validations'
import {
  timeZoneOptions,
  states,
  planOptions,
  holidayOptions,
  days,
} from './NumbersCallPlanForm.data'

const vMinDistributionRouting = vMinArrayLength(1)
const vMaxDistributionRouting = vMaxArrayLength(18)
const vMinVipRouting = vMinArrayLength(0)
const vMaxVipRouting = vMaxArrayLength(100)

const ForwardingNumberComponent = props => (
  <Field
    component={Input}
    label="Forwarding Number"
    validate={[vRequired, vStandardNumber]}
    {...props}
  />
)

const VipNumberNumberComponent = props => (
  <Field
    component={Input}
    label="VIP Phone Number"
    validate={[vRequired, vStandardNumber]}
    {...props}
  />
)

const planForms = {
  singleAnswerPoint: () => (
    <div>
      <div className={cn(sText.descrip3, sMarg.mb30)}>
        Basic answer point routing. All calls to your number will be redirected to the forwarding
        number you input below.
      </div>
      <Field
        name="singleAnswerPoint.forwardingNumber"
        component={Input}
        label="Forwarding Number"
        validate={[vRequired, vStandardNumber]}
      />
    </div>
  ),
  callOverflowRouting: () => (
    <div>
      <div className={cn(sText.descrip3, sMarg.mb30)}>
        The secondary number will be used when your primary number is busy or unanswered.
      </div>
      <Field
        name="callOverflowRouting.primaryForwardingNumber"
        component={Input}
        label="Primary Forwarding Number"
        validate={[vRequired, vStandardNumber]}
      />
      <Field
        name="callOverflowRouting.busyNoAnswerForwardingNumber"
        component={Input}
        label="Secondary Forwarding Number"
        validate={[vRequired, vStandardNumber]}
      />
    </div>
  ),
  callDistributionRouting: () => (
    <div>
      <div className={cn(sText.descrip3, sMarg.mb30)}>
        You can add between 1 and 18 forwarding numbers to a Distribution Routing plan. All these
        numbers will be called at the same time.
      </div>
      <FieldArray
        name="callDistributionRouting.forwardingNumbers"
        FieldComponent={ForwardingNumberComponent}
        component={FieldArrayRows}
        validate={[vMinDistributionRouting, vMaxDistributionRouting]}
      />
      <Field
        name="confirmFees"
        component={CheckboxRow}
        style={{ margin: '30px 0' }}
        validate={[vChecked]}
      >
        I understand that this call plan is associated with a monthly payment of $40 and an initial
        setup fee of $50.
      </Field>
    </div>
  ),
  vipServiceRouting: () => (
    <div>
      <div className={cn(sText.descrip3, sMarg.mb30)}>
        This allows you to redirect your VIPs to a VIP number. All other phone numbers will route to
        your Default Forwarding Number
      </div>
      <Field
        name="vipServiceRouting.defaultForwardingNumber"
        component={Input}
        label="Default Forwarding Number"
        validate={[vRequired, vStandardNumber]}
      />
      <Field
        name="vipServiceRouting.vipForwardingNumber"
        component={Input}
        label="VIP Forwarding Number"
        validate={[vRequired, vStandardNumber]}
      />
      <div className={cn(sText.descrip3, sMarg.mb30)}>Enter up to 100 VIP numbers.</div>
      <FieldArray
        name="vipServiceRouting.vipPhoneNumbers"
        FieldComponent={ForwardingNumberComponent}
        component={VipNumberNumberComponent}
        validate={[vMinVipRouting, vMaxVipRouting]}
      />
      <Field
        name="confirmFees"
        component={CheckboxRow}
        style={{ margin: '30px 0' }}
        validate={[vChecked]}
      >
        I understand that this call plan is associated with a monthly payment of $40 and an initial
        setup fee of $50.
      </Field>
    </div>
  ),
  stateRouting: () => (
    <div>
      <div className={cn(sText.descrip3, sMarg.mb30)}>
        Here you can route calls from each state to a different phone number. Make sure to fill out
        'unknown' for any number where the state cannot be determined.
      </div>
      {states.map(state => (
        <Field
          key={state}
          name={`stateRouting.forwardingNumbers.${state}`}
          component={Input}
          label={`Forwarding Number (${state})`}
          validate={[vRequired]}
        />
      ))}
      <Field
        name="confirmFees"
        component={CheckboxRow}
        style={{ margin: '30px 0' }}
        validate={[vChecked]}
      >
        I understand that this call plan is associated with a monthly payment of $200 and an initial
        setup fee of $50.
      </Field>
    </div>
  ),
  businessContinuity: () => (
    <div>
      <div className={cn(sText.descrip3, sMarg.mb30)}>
        Route calls to different numbers based on the operating hours of your business.
      </div>
      <Field
        name="businessContinuity.primaryForwardingNumber"
        component={Input}
        label="Forwarding Number - Primary"
        validate={[vRequired, vStandardNumber]}
      />
      <Field
        name="businessContinuity.busyNoAnswerOutageForwardingNumber"
        component={Input}
        label="Forwarding Number - Busy / No Answer"
        validate={[vRequired, vStandardNumber]}
      />
      <Field
        name="businessContinuity.afterHoursHolidaysForwardingNumber"
        component={Input}
        label="Forwarding Number - After hours"
        validate={[vRequired, vStandardNumber]}
      />
      <div className={cn(sText.descrip2, sMarg.mv30)}>Open Days:</div>
      <div className={cn(sLay['layout-row'], sLay['layout-wrap'])}>
        {days.map(day => (
          <div
            key={day}
            className={cn(sLay['flex-50'], sLay['layout-row'], sLay['layout-align-start-center'])}
            style={{ marginBottom: '10px' }}
          >
            <Field label={day} name={`businessContinuity.openDays.${day}`} component={Checkbox} />
            <span style={{ marginLeft: '10px' }}>{day}</span>
          </div>
        ))}
      </div>
      <div className={cn(sText.descrip2, sMarg.mv30)}>Open Hours:</div>
      <Row className={cn(sLay['layout-row'])}>
        <Col className={cn(sLay['flex-50'])}>
          <Field
            label="Open From"
            name="businessContinuity.openHours.from"
            component={Input}
            validate={[vRequired, v24HourTime]}
          />
        </Col>
        <Col className={cn(sLay['flex-50'])}>
          <Field
            label="Open To"
            name="businessContinuity.openHours.to"
            component={Input}
            validate={[vRequired, v24HourTime]}
          />
        </Col>
      </Row>
      <Field
        label="Time Zone"
        name="businessContinuity.timeZone"
        component={SelectPretty}
        options={timeZoneOptions}
        validate={[vRequired]}
      />
      <Field
        label="Public Holidays"
        name="businessContinuity.holidaySet"
        component={SelectPretty}
        options={holidayOptions}
        validate={[vRequired]}
      />
      <Field
        name="confirmFees"
        component={CheckboxRow}
        style={{ margin: '30px 0' }}
        validate={[vChecked]}
      >
        I understand that this call plan is associated with a monthly payment of $40 and an initial
        setup fee of $50.
      </Field>
    </div>
  ),
}

export default class NumbersCallPlanForm extends Component {
  render() {
    const {
      handleSubmit,
      changeCallPlan,
      formData,
      submitting,
      onCancel,
      afterSubmit,
      serviceId,
      invalid,
    } = this.props

    const FormComponent = planForms[formData.planType]
    const onSubmit = handleSubmit(data => changeCallPlan(serviceId, data).then(afterSubmit))

    return (
      <form onSubmit={onSubmit}>
        <Field
          name="planType"
          options={planOptions}
          component={SelectPretty}
          label="Plan Type"
          validate={[vRequired]}
        />
        {FormComponent && <FormComponent />}
        <div
          className={cn(sLay['layout-column'], sLay['layout-align-center-center'])}
          style={{ marginTop: '40px' }}
        >
          <Button
            color="tertiary"
            type="submit"
            loading={submitting}
            disabled={submitting || invalid}
          >
            Submit
          </Button>
          <br />
          <br />
          <Button plain onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    )
  }
}



// WEBPACK FOOTER //
// ./~/@pickle/pickle-shared/src/features/Numbers/NumbersCallPlanForm/NumbersCallPlanForm.jsx